import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getAssetEvents } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { formatDateTime, getEventTypeLabel, formatCurrency } from '../lib/utils';
import { 
  Scan, Send, Package, Trash2, Wrench, Edit, 
  ArrowRight, Building, MapPin, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const eventIcons = {
  SCANNED_IN: Scan,
  SENT_REPAIR: Wrench,
  SENT_LOCATION: Send,
  RECEIVED: Package,
  SENT_TRASH: Trash2,
  UPDATED: Edit,
  FIELD_UPDATED: Edit,
  REMOVAL_REQUESTED: AlertCircle,
  REMOVAL_APPROVED: CheckCircle,
  REMOVAL_DENIED: XCircle,
  EDIT_REQUESTED: AlertCircle,
  EDIT_APPROVED: CheckCircle,
  EDIT_DENIED: XCircle,
  ASSET_REMOVED: Trash2,
};

const eventColors = {
  SCANNED_IN: 'bg-emerald-500',
  SENT_REPAIR: 'bg-amber-500',
  SENT_LOCATION: 'bg-blue-500',
  RECEIVED: 'bg-green-500',
  SENT_TRASH: 'bg-red-500',
  UPDATED: 'bg-purple-500',
  FIELD_UPDATED: 'bg-purple-500',
  REMOVAL_REQUESTED: 'bg-orange-500',
  REMOVAL_APPROVED: 'bg-green-500',
  REMOVAL_DENIED: 'bg-red-500',
  EDIT_REQUESTED: 'bg-yellow-500',
  EDIT_APPROVED: 'bg-green-500',
  EDIT_DENIED: 'bg-red-500',
  ASSET_REMOVED: 'bg-red-600',
};

export default function AssetHistory({ open, onClose, asset }) {
  const { getLocationName, getRepairShopName } = useApp();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    if (!asset) return;
    setLoading(true);
    try {
      const res = await getAssetEvents(asset.id);
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  }, [asset]);

  useEffect(() => {
    if (open && asset) {
      loadEvents();
    }
  }, [open, asset, loadEvents]);

  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A';
    if (key === 'purchase_cost') return formatCurrency(value);
    if (key === 'purchase_date') return value;
    return String(value);
  };

  const renderFieldChanges = (fieldChanges) => {
    if (!fieldChanges || Object.keys(fieldChanges).length === 0) return null;
    
    return (
      <div className="mt-2 space-y-1 text-xs bg-gray-50 rounded p-2">
        {Object.entries(fieldChanges).map(([field, change]) => (
          <div key={field} className="flex items-start gap-1">
            <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span>
            <span className="text-gray-500">{formatFieldValue(field, change.old)}</span>
            <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
            <span className="text-purple-600">{formatFieldValue(field, change.new)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderEventDetails = (event) => {
    switch (event.event_type) {
      case 'SCANNED_IN':
        return (
          <div className="text-sm text-gray-600">
            <p>Added to <span className="font-medium">{event.to_location_name || getLocationName(event.to_location_id)}</span></p>
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      case 'SENT_REPAIR':
        return (
          <div className="text-sm text-gray-600 space-y-1">
            <p className="flex items-center gap-1">
              <Building className="h-3 w-3" />
              Shop: <span className="font-medium">{getRepairShopName(event.repair_shop_id)}</span>
            </p>
            {event.reason && <p>Reason: {event.reason}</p>}
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      case 'SENT_LOCATION':
        return (
          <div className="text-sm text-gray-600">
            <p className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {event.from_location_name || getLocationName(event.from_location_id)}
              <ArrowRight className="h-3 w-3" />
              {event.to_location_name || getLocationName(event.to_location_id)}
            </p>
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      case 'RECEIVED':
        return (
          <div className="text-sm text-gray-600">
            <p>Received at <span className="font-medium">{event.to_location_name || getLocationName(event.to_location_id)}</span></p>
            {event.reason && <p className="text-xs">{event.reason}</p>}
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      case 'SENT_TRASH':
        return (
          <div className="text-sm text-gray-600 space-y-1">
            {event.reason && <p>Reason: {event.reason}</p>}
            {event.approved_by_note && <p className="text-xs">Note: {event.approved_by_note}</p>}
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      case 'UPDATED':
      case 'FIELD_UPDATED':
        return (
          <div className="text-sm text-gray-600">
            <p>{event.reason || 'Asset information updated'}</p>
            {renderFieldChanges(event.field_changes)}
            {event.created_by_name && <p className="text-xs text-gray-500 mt-1">By: {event.created_by_name}</p>}
          </div>
        );
      case 'REMOVAL_REQUESTED':
      case 'EDIT_REQUESTED':
        return (
          <div className="text-sm text-gray-600">
            <p>{event.reason || 'Request submitted'}</p>
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      case 'REMOVAL_APPROVED':
      case 'EDIT_APPROVED':
        return (
          <div className="text-sm text-gray-600">
            <p className="text-green-600">Request approved</p>
            {event.approved_by_note && <p className="text-xs">Note: {event.approved_by_note}</p>}
            {renderFieldChanges(event.field_changes)}
            {event.created_by_name && <p className="text-xs text-gray-500 mt-1">By: {event.created_by_name}</p>}
          </div>
        );
      case 'REMOVAL_DENIED':
      case 'EDIT_DENIED':
        return (
          <div className="text-sm text-gray-600">
            <p className="text-red-600">Request denied</p>
            {event.approved_by_note && <p className="text-xs">Reason: {event.approved_by_note}</p>}
            {event.created_by_name && <p className="text-xs text-gray-500 mt-1">By: {event.created_by_name}</p>}
          </div>
        );
      case 'ASSET_REMOVED':
        return (
          <div className="text-sm text-gray-600">
            <p className="text-red-600">Asset permanently removed</p>
            {event.reason && <p className="text-xs">{event.reason}</p>}
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
      default:
        return (
          <div className="text-sm text-gray-600">
            {event.reason && <p>{event.reason}</p>}
            {event.created_by_name && <p className="text-xs text-gray-500">By: {event.created_by_name}</p>}
          </div>
        );
    }
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Asset History</DialogTitle>
        </DialogHeader>

        {/* Asset Summary */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
          <p className="font-mono text-lg font-bold">{asset.barcode}</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            {asset.purchase_date && (
              <div>
                <span className="text-gray-500">Purchase Date:</span>{' '}
                <span className="font-medium">{asset.purchase_date}</span>
              </div>
            )}
            {asset.purchase_cost && (
              <div>
                <span className="text-gray-500">Purchase Cost:</span>{' '}
                <span className="font-medium">{formatCurrency(asset.purchase_cost)}</span>
              </div>
            )}
            {asset.first_received_date && (
              <div>
                <span className="text-gray-500">First Received:</span>{' '}
                <span className="font-medium">{asset.first_received_date}</span>
              </div>
            )}
            {asset.make && (
              <div>
                <span className="text-gray-500">Make:</span>{' '}
                <span className="font-medium">{asset.make}</span>
              </div>
            )}
            {asset.model && (
              <div>
                <span className="text-gray-500">Model:</span>{' '}
                <span className="font-medium">{asset.model}</span>
              </div>
            )}
            {asset.serial_number && (
              <div className="col-span-2">
                <span className="text-gray-500">Serial:</span>{' '}
                <span className="font-medium font-mono">{asset.serial_number}</span>
              </div>
            )}
          </div>
        </div>

        <ScrollArea className="h-[350px] pr-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No history available</div>
          ) : (
            <div className="space-y-4">
              {events.map((event, index) => {
                const Icon = eventIcons[event.event_type] || Edit;
                const colorClass = eventColors[event.event_type] || 'bg-gray-500';
                
                return (
                  <div key={event.id} className="relative flex gap-4">
                    {/* Timeline line */}
                    {index < events.length - 1 && (
                      <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" />
                    )}
                    
                    {/* Icon */}
                    <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full ${colorClass} flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {getEventTypeLabel(event.event_type)}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(event.event_date || event.created_at)}
                        </span>
                      </div>
                      {renderEventDetails(event)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
